import { t } from "@/lib/i18n/interpolate";
import type { DocSection, DocumentModel } from "@/lib/tool-kit/document";
import { paragraphsOrPrompt } from "@/lib/tool-kit/essay-templates/text";

/**
 * A reflective piece in the six stages of Gibbs' Reflective Cycle, from Graham Gibbs's
 * 1988 book *Learning by Doing*: the writer's own words for each stage, in order, with the
 * stage's guiding question in place of any stage not yet written — and the model's
 * reference at the end, since a reflection that uses a model is expected to cite it.
 */

export const GIBBS_STAGES = [
  "description",
  "feelings",
  "evaluation",
  "analysis",
  "conclusion",
  "action",
] as const;

export type GibbsStage = (typeof GIBBS_STAGES)[number];

export type GibbsInput = Record<GibbsStage, string> & {
  topic: string;
  /** One heading per stage, or continuous prose for assignments that want an essay. */
  headings: boolean;
};

export interface GibbsLabels {
  defaultTitle: string;
  titleWithTopic: string;
  subtitle: string;
  voiceNote: string;
  stageHeading: string;
  stagePrompt: string;
  stagePromptNamed: string;
  stageNames: Record<GibbsStage, string>;
  stageQuestions: Record<GibbsStage, string>;
  stageGuides: Record<GibbsStage, string>;
  referenceHeading: string;
  referenceNote: string;
  reference: { before: string; title: string; after: string };
}

export function buildGibbsReflection(
  input: GibbsInput,
  labels: GibbsLabels
): DocumentModel {
  const topic = input.topic.trim();
  const stages: DocSection[] = GIBBS_STAGES.map((stage, index) => {
    const values = {
      stage: labels.stageNames[stage],
      question: labels.stageQuestions[stage],
      guide: labels.stageGuides[stage],
    };
    const prompt = t(
      input.headings ? labels.stagePrompt : labels.stagePromptNamed,
      values
    );
    return {
      heading: input.headings
        ? t(labels.stageHeading, { n: index + 1, stage: values.stage })
        : undefined,
      blocks: paragraphsOrPrompt(input[stage], prompt),
    };
  });
  return {
    title: topic ? t(labels.titleWithTopic, { topic }) : labels.defaultTitle,
    subtitle: labels.subtitle,
    sections: [
      { blocks: [{ kind: "guidance", text: labels.voiceNote }] },
      ...stages,
      {
        heading: labels.referenceHeading,
        blocks: [
          {
            kind: "paragraph",
            text: [
              { text: labels.reference.before },
              { text: labels.reference.title, italic: true },
              { text: labels.reference.after },
            ],
          },
          { kind: "guidance", text: labels.referenceNote },
        ],
      },
    ],
  };
}
